import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuDownload, LuEye, LuFile, LuFileText, LuFolderOpen, LuLoaderCircle } from 'react-icons/lu';
import { downloadFileAs } from '../../utils/downloadFile';
import {
  fetchEmployeeCvDocuments,
  groupCvDocuments,
  getMissingRequiredDocuments,
} from '../../utils/cvDocuments';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { EmptyState, HelpNote } from '../ProfilePage/components/ProfileUi';
import { GLASS_CHIP, GLASS_PRESSABLE } from '../../components/ui/glass';
import useSmoText from './smoText';

// Çalışan Yönetimi'ndeki "CV/GCP/IATA Belgeleri" bölümüyle aynı kaynak ve
// aynı sıra (bkz. utils/cvDocuments). SMO tarafında yalnızca görüntüleme ve
// indirme var; yükleme ve silme yok.
const fileIcon = (fileType = '') => {
  const type = String(fileType).toLowerCase();
  if (type.includes('pdf')) return <LuFileText aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-rose-500" />;
  if (type.includes('doc') || type.includes('word')) return <LuFileText aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-sky-500" />;
  return <LuFile aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-slate-400" />;
};

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size === 0) return '';
  return `${(size / 1024).toFixed(2)} KB`;
};

const ACTION = `tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-600 hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-text-white ${GLASS_CHIP} ${GLASS_PRESSABLE}`;

/** A team member's CV / GCP / IATA documents (view and download only). */
const EmployeeDocumentsModal = ({ show, onHide, employee }) => {
  const { t, label, language, locale } = useSmoText();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!show || !employee?.id) return;

    let cancelled = false;
    const fetchDocuments = async () => {
      setLoading(true);
      setError(false);
      try {
        const docs = await fetchEmployeeCvDocuments(employee.id);
        if (!cancelled) setDocuments(docs);
      } catch (err) {
        console.error('CV/GCP/IATA belgeleri yüklenemedi:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [show, employee?.id]);

  const groups = groupCvDocuments(documents);
  const missingRequired = getMissingRequiredDocuments(documents);

  return (
    <Modal
      isOpen={!!show}
      onClose={onHide}
      size="md"
      title={label('documents.title')}
      subtitle={employee ? `${employee.name || ''} ${employee.surname || ''}`.trim() : ''}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuFolderOpen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onHide}>{t('common.close')}</Button>}
    >
      <div className="tw-space-y-4">
        {/* Hiç yüklenmemiş zorunlu belgeler tek satırda hatırlatılır */}
        {!loading && !error && missingRequired.length > 0 && (
          <HelpNote tone="warning">{label('documents.missing', { list: missingRequired.map((type) => type.label).join(', ') })}</HelpNote>
        )}
        {loading ? (
          <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
            {label('documents.loading')}
          </p>
        ) : error ? (
          <HelpNote tone="danger">{label('documents.error')}</HelpNote>
        ) : groups.length === 0 ? (
          <EmptyState icon={LuFolderOpen} title={label('documents.empty')} />
        ) : (
          groups.map((group) => (
            <section key={group.key} className="tw-space-y-2">
              <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">
                {group.label}
                <span className="tw-rounded-full tw-bg-slate-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-tabular-nums">{group.docs.length}</span>
              </h3>
              <ul className="tw-space-y-2">
                {group.docs.map((doc) => (
                  <li key={doc.id} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <span className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-slate-500/[0.07] dark:tw-bg-white/[0.05]">{fileIcon(doc.fileType)}</span>
                    <span className="tw-min-w-0 tw-flex-1">
                      <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white" title={doc.fileName}>{doc.fileName}</span>
                      <span className="tw-block tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                        {[formatDate(doc.uploadedAt, locale), formatSize(doc.fileSize)].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className={ACTION} title={label('documents.view')} aria-label={label('documents.view')}>
                      <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </a>
                    <button type="button" className={ACTION} onClick={() => downloadFileAs(doc.fileUrl, doc.fileName)} title={label('documents.download')} aria-label={label('documents.download')}>
                      <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
        <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('documents.help')}</p>
      </div>
    </Modal>
  );
};

EmployeeDocumentsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  employee: PropTypes.object,
};

export default EmployeeDocumentsModal;
